import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  associateWebAuthnCredential,
  deleteWebAuthnCredential,
  listWebAuthnCredentials,
  type AuthWebAuthnCredential,
} from 'aws-amplify/auth';
import { AUTH_MODE, useAuth } from './auth';

/*
 * Passkeys (Cognito WebAuthn). Sign-in only offers a passkey where this
 * device can use one: a built-in authenticator (Touch ID, Windows Hello,
 * Android screen lock) or a nearby phone over hybrid transport. Browsers
 * don't reveal whether an account has a passkey before trying, by design.
 */

type ClientCapabilities = Record<string, boolean | undefined>;
type PublicKeyCredentialStatics = {
  getClientCapabilities?: () => Promise<ClientCapabilities>;
  isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean>;
};

export async function deviceCanUsePasskeys(): Promise<boolean> {
  if (AUTH_MODE !== 'cognito' || typeof window === 'undefined') return false;
  if (!('PublicKeyCredential' in window)) return false;
  const pkc = window.PublicKeyCredential as unknown as PublicKeyCredentialStatics;
  try {
    if (pkc.getClientCapabilities) {
      const caps = await pkc.getClientCapabilities();
      if (caps.passkeyPlatformAuthenticator || caps.hybridTransport) return true;
    }
    return (await pkc.isUserVerifyingPlatformAuthenticatorAvailable?.()) ?? false;
  } catch {
    return false;
  }
}

/** Whether this device can sign in with (or create) a passkey. Checked once per page load. */
export function useCanUsePasskeys(): boolean {
  const q = useQuery({
    queryKey: ['passkeyCapable'],
    queryFn: deviceCanUsePasskeys,
    staleTime: Infinity,
  });
  return q.data ?? false;
}

export interface Passkey {
  id: string;
  name: string;
  createdAt: Date | null;
  /** "platform" (this kind of device) or "cross-platform" (security key, phone). */
  attachment: string | null;
}

function toPasskey(c: AuthWebAuthnCredential): Passkey {
  return {
    id: c.credentialId ?? '',
    name: c.friendlyCredentialName || 'Passkey',
    createdAt: c.createdAt ?? null,
    attachment: c.authenticatorAttachment ?? null,
  };
}

/** The signed-in user's passkeys (Cognito only). */
export function usePasskeys() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['passkeys'],
    enabled: AUTH_MODE === 'cognito' && Boolean(user),
    queryFn: async () => {
      const all: Passkey[] = [];
      let nextToken: string | undefined;
      do {
        const page = await listWebAuthnCredentials({ pageSize: 20, nextToken });
        all.push(...page.credentials.map(toPasskey));
        nextToken = page.nextToken;
      } while (nextToken);
      return all;
    },
  });
}

export function useAddPasskey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => associateWebAuthnCredential(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['passkeys'] }),
  });
}

export function useRemovePasskey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentialId: string) => deleteWebAuthnCredential({ credentialId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['passkeys'] }),
  });
}
